import { SinoDeAvisos } from "./SinoDeAvisos"
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react"

import type { Conta } from "../api"
import logoAthusContratos from "../estilo/athus_contrato_logo.png"
import { Button } from "../componentes/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../componentes/DropdownMenu"
import { Selo } from "../componentes/Selo"
import { Separator } from "../componentes/Separator"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../componentes/Breadcrumb"
import * as Icones from "../componentes/Icones"
import { BOTAO_DA_CASCA } from "../lib/classes"
import { cn } from "../lib/cn"
import { useLarguraAte } from "../lib/use-largura-ate"
import { data } from "../formato"
import type { NivelDaTrilha } from "./trilha"
import { AreaDoUsuario } from "./AreaDoUsuario"
import type { AbaDoPerfil } from "../telas/Perfil"
import {
  TEMA_TROCADO,
  aplicarTema,
  temaGuardado,
  type PosicaoDaBarra,
  type Tema,
} from "../preferencias"

const COM_FAIXA_E_TRILHO_A_ESQUERDA =
  "min-[901px]:grid-cols-[var(--trilho-largura)_minmax(0,1fr)] min-[901px]:grid-rows-[auto_1fr] min-[901px]:[grid-template-areas:'faixa_faixa'_'trilho_corpo']"

const COM_FAIXA_E_TRILHO_A_DIREITA =
  "min-[901px]:grid-cols-[minmax(0,1fr)_var(--trilho-largura)] min-[901px]:grid-rows-[auto_1fr] min-[901px]:[grid-template-areas:'faixa_faixa'_'corpo_trilho']"

const GRADE: Record<PosicaoDaBarra, string> = {
  topo: "min-[901px]:grid-rows-[auto_1fr] min-[901px]:[grid-template-areas:'faixa'_'corpo']",
  esquerda: COM_FAIXA_E_TRILHO_A_ESQUERDA,
  direita: COM_FAIXA_E_TRILHO_A_DIREITA,
  "topo-esquerda": COM_FAIXA_E_TRILHO_A_ESQUERDA,
  "topo-direita": COM_FAIXA_E_TRILHO_A_DIREITA,
}

const ITEM_DA_FAIXA =
  "my-2 flex min-h-10 items-center gap-2 rounded-(--raio-2) border border-transparent px-3.5 text-(length:--texto-md) font-semibold whitespace-nowrap text-(--azul-100) no-underline hover:text-(--rail-tinta-forte) aria-disabled:cursor-not-allowed aria-[current=page]:border-(--azul-300) aria-[current=page]:bg-(--painel) aria-[current=page]:text-(--marca-escura) aria-[current=page]:shadow-(--elev-1)"

const ITEM_DO_TRILHO =
  "flex items-center gap-2 rounded-(--raio-2) border border-transparent p-2 text-base text-(--azul-100) no-underline hover:text-(--rail-tinta-forte) aria-disabled:cursor-not-allowed aria-[current=page]:border-(--azul-300) aria-[current=page]:bg-(--painel) aria-[current=page]:font-semibold aria-[current=page]:text-(--marca-escura) aria-[current=page]:shadow-(--elev-1) min-[901px]:group-data-[recolhido=true]/trilho:justify-center [&>svg]:shrink-0"

export type ItemDoSubitem = {
  chave: string
  rotulo: string
  endereco: string
  icone: Icones.Icone
}

export type SubitemDoTrilho = {
  chave: string
  rotulo: string
  endereco: string
  icone?: Icones.Icone
  itens?: ItemDoSubitem[]
}

export type ItemDoTrilho<C extends string = string> = {
  chave: C
  rotulo: string
  icone: Icones.Icone
  emBreve?: boolean
  subitens?: SubitemDoTrilho[]
}

export type SecaoDoTrilho<C extends string = string> = {
  titulo: string
  itens: ItemDoTrilho<C>[]
}

const SUBITEM_DA_LISTA =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-(length:--texto-base) text-(--tinta-2) no-underline hover:bg-(--painel-alt) hover:text-(--tinta) aria-[current=page]:bg-(--painel-alt) aria-[current=page]:font-semibold aria-[current=page]:text-(--nav-tinta-ativa) [&>svg]:shrink-0"

const SUBITEM_NA_FAIXA =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-(length:--texto-base) text-(--azul-100) no-underline hover:text-(--rail-tinta-forte) aria-[current=page]:bg-(--painel) aria-[current=page]:font-semibold aria-[current=page]:text-(--marca-escura) [&>svg]:shrink-0"

const OPCAO_MARCADA = "bg-(--selecionado) font-semibold"

const ESPERA_PARA_FECHAR = 200

type PropsDoSubmenu = {
  item: ItemDoTrilho
  ativo: boolean
  subitemAtual: string | null
  itemAtual: string | null
  aoEscolher: (sub: string, item?: string) => void
}

function SubmenuFlutuante({
  item,
  ativo,
  subitemAtual,
  itemAtual,
  aoEscolher,
  lado,
  classe,
  rotuloVisivel,
}: PropsDoSubmenu & { lado: "right" | "left" | "bottom"; classe: string; rotuloVisivel: ReactNode }) {
  const [aberto, definirAberto] = useState(false)
  const espera = useRef<number | null>(null)
  const peloMouse = useRef(false)
  const Icone = item.icone
  const subitens = item.subitens ?? []

  const cancelar = () => {
    if (espera.current !== null) {
      window.clearTimeout(espera.current)
      espera.current = null
    }
  }
  const abrir = () => {
    cancelar()
    peloMouse.current = true
    definirAberto(true)
  }
  const fecharLogo = () => {
    cancelar()
    espera.current = window.setTimeout(() => {
      espera.current = null
      definirAberto(false)
    }, ESPERA_PARA_FECHAR)
  }
  useEffect(() => cancelar, [])

  const doMouse = (evento: PointerEvent<HTMLElement>) => evento.pointerType !== "touch" && evento.pointerType !== "pen"
  const escolher = (sub: string, alvo?: string) => {
    cancelar()
    definirAberto(false)
    aoEscolher(sub, alvo)
  }

  return (
    <DropdownMenu
      modal={false}
      open={aberto}
      onOpenChange={(abrirAgora) => {
        if (abrirAgora) peloMouse.current = false
        definirAberto(abrirAgora)
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={classe}
          aria-label={item.rotulo}
          aria-current={ativo ? "page" : undefined}
          title={item.rotulo}
          onPointerEnter={(evento) => doMouse(evento) && abrir()}
          onPointerLeave={(evento) => doMouse(evento) && fecharLogo()}
          onPointerDown={(evento) => {
            if (!doMouse(evento)) return
            evento.preventDefault()
            abrir()
          }}
        >
          <Icone tamanho={16} />
          {rotuloVisivel}
          <span className="ml-auto inline-flex min-[901px]:group-data-[recolhido=true]/trilho:hidden">
            {lado === "bottom" ? <Icones.Aumentar tamanho={14} /> : <Icones.Proxima tamanho={14} />}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={lado}
        align="start"
        sideOffset={lado === "bottom" ? 4 : 6}
        className="min-w-56"
        onPointerEnter={cancelar}
        onPointerLeave={(evento) => doMouse(evento) && fecharLogo()}
        onCloseAutoFocus={(evento) => {
          if (peloMouse.current) evento.preventDefault()
        }}
      >
        {subitens.map((sub) => {
          const IconeDoGrupo = sub.icone
          const grupoMarcado = ativo && sub.chave === subitemAtual
          const conteudo = (
            <>
              {IconeDoGrupo !== undefined && <IconeDoGrupo tamanho={16} />}
              {sub.rotulo}
            </>
          )
          if (sub.itens === undefined || sub.itens.length === 0) {
            return (
              <DropdownMenuItem
                key={sub.chave}
                aria-current={grupoMarcado ? "true" : undefined}
                className={cn(grupoMarcado && OPCAO_MARCADA)}
                onSelect={() => escolher(sub.chave)}
              >
                {conteudo}
              </DropdownMenuItem>
            )
          }
          return (
            <DropdownMenuSub key={sub.chave}>
              <DropdownMenuSubTrigger
                aria-current={grupoMarcado ? "true" : undefined}
                className={cn("cursor-pointer", grupoMarcado && OPCAO_MARCADA)}
                onClick={() => escolher(sub.chave)}
              >
                {conteudo}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                sideOffset={4}
                className="min-w-56 shadow-md"
                onPointerEnter={cancelar}
              >
                {sub.itens.map((filho) => {
                  const IconeDoItem = filho.icone
                  const itemMarcado = grupoMarcado && filho.chave === itemAtual
                  return (
                    <DropdownMenuItem
                      key={filho.chave}
                      aria-current={itemMarcado ? "page" : undefined}
                      className={cn(itemMarcado && OPCAO_MARCADA)}
                      onSelect={() => escolher(sub.chave, filho.chave)}
                    >
                      <IconeDoItem tamanho={16} />
                      {filho.rotulo}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SubmenuNaLista({
  item,
  ativo,
  subitemAtual,
  aoEscolher,
  classeDoTitulo,
  classeDoSubitem = SUBITEM_DA_LISTA,
  classeDaGuia = "border-(--borda)",
}: PropsDoSubmenu & { classeDoTitulo: string; classeDoSubitem?: string; classeDaGuia?: string }) {
  const Icone = item.icone
  return (
    <div className="flex flex-col">
      <span className={classeDoTitulo}>
        <Icone tamanho={16} />
        {item.rotulo}
      </span>
      <ul className={cn("mt-1 ml-[17px] flex list-none flex-col gap-0.5 border-l p-0 pl-2", classeDaGuia)}>
        {(item.subitens ?? []).map((sub) => {
          const IconeDoGrupo = sub.icone
          return (
            <li key={sub.chave}>
              <a
                className={classeDoSubitem}
                href={sub.endereco}
                aria-current={ativo && sub.chave === subitemAtual ? "page" : undefined}
                onClick={(evento: MouseEvent<HTMLAnchorElement>) => {
                  evento.preventDefault()
                  aoEscolher(sub.chave)
                }}
              >
                {IconeDoGrupo !== undefined && <IconeDoGrupo tamanho={16} />}
                {sub.rotulo}
              </a>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

type Props<C extends string> = {
  secoes: SecaoDoTrilho<C>[]
  atual: C
  aoTrocar: (chave: C) => void
  subitemAtual?: string | null
  itemDoSubitemAtual?: string | null
  aoTrocarSubitem?: (chave: C, sub: string, item?: string) => void
  aoAbrirAvisos: () => void
  aoAbrirContratoDoAviso: (id: number) => void
  aoAbrirPerfil: (aba: AbaDoPerfil) => void
  trilha: NivelDaTrilha[]
  conta: Conta
  aoSair: () => void
  aoTrocarEntidade?: (entidadeId: number) => Promise<void>
  posicao: PosicaoDaBarra
  busca?: ReactNode
  children: ReactNode
}

const FAIXA_DA_ENTIDADE_DESATIVADA = "rounded-(--raio-controle) border border-(--status-atencao) bg-(--status-atencao) px-3 py-2 text-(length:--texto-sm) font-medium text-(--sobre-status-atencao)" // anti-ia-ok: token de cor de status, não texto de tela

export function Casca<C extends string>({
  secoes,
  atual,
  aoTrocar,
  subitemAtual = null,
  itemDoSubitemAtual = null,
  aoTrocarSubitem,
  aoAbrirAvisos,
  aoAbrirContratoDoAviso,
  aoAbrirPerfil,
  trilha,
  conta,
  aoSair,
  aoTrocarEntidade,
  posicao,
  busca,
  children,
}: Props<C>) {
  const acoesNaFaixa = posicao.startsWith("topo")
  const temTrilho = posicao !== "topo"
  const trilhoADireita = posicao === "direita" || posicao === "topo-direita"
  const itens = secoes.flatMap((secao) => secao.itens)
  const celular = useLarguraAte(900)

  const marca = (
    <div className="flex flex-none items-center gap-3">
      <span
        className="grid size-10 flex-none place-items-center rounded-(--raio-2) border border-(--azul-300) bg-(--branco)"
        aria-hidden="true"
      >
        <img className="block size-[30px] object-contain" src={logoAthusContratos} alt="" />
      </span>
      <div className="text-(length:--texto-xl) leading-[1.1] font-bold max-[767px]:text-(length:--texto-lg) tracking-(--tracking-marca) whitespace-nowrap text-(--rail-tinta-forte)">
        ATHUS Contratos
      </div>
    </div>
  )

  const [trocando, definirTrocando] = useState(false)
  const [erroDaTroca, definirErroDaTroca] = useState<string | null>(null)
  const entidades = conta.entidades ?? []
  const podeTrocar = aoTrocarEntidade !== undefined && conta.entidade !== null && entidades.length > 1

  const trocarPara = (id: number) => {
    if (aoTrocarEntidade === undefined || id === conta.entidade?.id || trocando) return
    definirTrocando(true)
    definirErroDaTroca(null)
    aoTrocarEntidade(id)
      .catch((falha: unknown) => {
        definirErroDaTroca(falha instanceof Error ? falha.message : "Não foi possível trocar de entidade.")
      })
      .finally(() => definirTrocando(false))
  }

  const NOME_DA_ENTIDADE =
    "truncate text-(length:--texto-base) font-semibold text-(--tinta) in-data-[slot=faixa]:text-(--rail-tinta-forte)"

  const contexto = podeTrocar ? (
    <div className="ml-4 flex min-w-0 flex-col gap-0.5 border-l border-(--azul-400) pl-6 max-[820px]:ml-0 max-[820px]:border-l-0 max-[820px]:pl-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex min-w-0 cursor-pointer items-center gap-2 rounded-(--raio-2) border border-transparent bg-transparent px-1.5 py-1 text-left hover:border-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current disabled:cursor-wait in-data-[slot=faixa]:text-(--rail-tinta-forte)"
            aria-label={`Trocar de entidade. Atual: ${conta.entidade?.nome ?? ""}`}
            disabled={trocando}
          >
            <Icones.Orgao tamanho={16} />
            <strong className={cn(NOME_DA_ENTIDADE, "max-[820px]:hidden")}>{conta.entidade?.nome}</strong>
            <strong className={cn(NOME_DA_ENTIDADE, "min-[821px]:hidden")}>{conta.entidade?.sigla}</strong>
            <Icones.Recalcular tamanho={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-64">
          <DropdownMenuLabel>Alterar para:</DropdownMenuLabel>
          {entidades.map((entidade) => {
            const atual = entidade.id === conta.entidade?.id
            return (
              <DropdownMenuItem
                key={entidade.id}
                aria-label={`${entidade.nome} (${entidade.sigla})${atual ? ", atual" : ""}`}
                aria-current={atual ? "true" : undefined}
                className={cn("items-start", atual && "bg-(--selecionado)")}
                onSelect={() => trocarPara(entidade.id)}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-semibold">{entidade.sigla}</span>
                  <span className="truncate text-(length:--texto-xs) text-(--tinta-2)">{entidade.nome}</span>
                </span>
                {atual && <Icones.Marcado tamanho={16} />}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {erroDaTroca !== null && (
        <span role="alert" className="text-(length:--texto-xs) text-(--vermelho)">
          {erroDaTroca}
        </span>
      )}
    </div>
  ) : (
    <div className="ml-4 flex min-w-0 flex-col gap-0.5 border-l border-(--azul-400) pl-6 max-[820px]:hidden">
      <strong className={NOME_DA_ENTIDADE}>
        {conta.entidade !== null ? conta.entidade.nome : "Todas as entidades"}
      </strong>
    </div>
  )

  const [tema, definirTema] = useState<Tema>(() => temaGuardado())
  useEffect(() => {
    const aoTrocar = (evento: Event) => definirTema((evento as CustomEvent<Tema>).detail)
    window.addEventListener(TEMA_TROCADO, aoTrocar)
    return () => window.removeEventListener(TEMA_TROCADO, aoTrocar)
  }, [])
  const [recolhido, definirRecolhido] = useState(false)
  const [menuAberto, definirMenuAberto] = useState(false)
  const rotuloAtual = itens.find((item) => item.chave === atual)?.rotulo ?? ""

  const virarTema = () => {
    const outro: Tema = tema === "escuro" ? "claro" : "escuro"
    aplicarTema(outro)
    definirTema(outro)
  }

  const acoes = (
    <div className="flex min-w-0 flex-auto items-center gap-4">
      {contexto}
      <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2">
        {busca}
        {conta.is_root && <Selo situacao="root" />}
        <SinoDeAvisos
          entidadeId={conta.entidade?.id ?? null}
          aoVerTodos={aoAbrirAvisos}
          aoAbrirContrato={aoAbrirContratoDoAviso}
        />
        <button
          type="button"
          className={BOTAO_DA_CASCA}
          onClick={virarTema}
          title={tema === "escuro" ? "Usar tema claro" : "Usar tema escuro"}
          aria-label={tema === "escuro" ? "Usar tema claro" : "Usar tema escuro"}
        >
          {tema === "escuro" ? <Icones.Sol /> : <Icones.Lua />}
        </button>
        <Separator
          orientation="vertical"
          data-slot="separador-da-conta"
          className="mx-1 data-[orientation=vertical]:h-6 bg-(--borda-forte) in-data-[slot=faixa]:bg-[rgb(255_255_255/0.28)]" // anti-ia-ok: o mesmo branco translúcido do BOTAO_DA_CASCA
        />
        <AreaDoUsuario conta={conta} aoAbrirPerfil={aoAbrirPerfil} />
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 rounded-(--raio-2) border border-transparent bg-(--azul-600) px-2 py-1.5 text-(length:--texto-sm) text-(--azul-100) hover:border-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
          onClick={aoSair}
        >
          <Icones.Sair />
          Sair
        </button>
      </div>
    </div>
  )

  const botaoMenu = (
    <button
      type="button"
      data-slot="menu-celular"
      className={cn(BOTAO_DA_CASCA, "ml-auto min-[901px]:hidden")}
      aria-expanded={menuAberto}
      aria-controls="menu-principal"
      onClick={() => definirMenuAberto((aberto) => !aberto)}
    >
      <Icones.Menu tamanho={16} />
      <span>Menu</span>
      <span className="font-normal">· {rotuloAtual}</span>
    </button>
  )

  return (
    <div
      data-slot="casca"
      data-barra={posicao}
      style={recolhido ? ({ "--trilho-largura": "56px" } as CSSProperties) : undefined}
      className={cn(
        "grid min-h-screen grid-cols-[minmax(0,1fr)] grid-rows-[auto_auto_1fr] [grid-template-areas:'faixa'_'trilho'_'corpo']",
        GRADE[posicao],
      )}
    >
      <header
        data-slot="faixa"
        className={cn(
          "z-40 flex flex-col border-b border-(--borda) bg-(--superficie) text-(--tinta) [grid-area:faixa]",
          posicao === "topo" && "min-[901px]:sticky min-[901px]:-top-[57px]",
        )}
      >
        <div className="flex min-h-[57px] flex-wrap items-center gap-x-4 gap-y-2 bg-(image:--fusao-topo) px-5 text-(--marca-texto) max-[767px]:px-3 max-[767px]:py-2">
          {marca}
          {acoesNaFaixa && acoes}
          {botaoMenu}
        </div>

        {posicao === "topo" && (
          <nav
            id="menu-principal"
            data-menu={menuAberto ? "aberto" : "fechado"}
            className="flex min-w-0 items-stretch gap-0.5 overflow-x-auto border-t border-(--borda) bg-(image:--fusao-realce) px-3 max-[901px]:flex-col max-[901px]:overflow-x-visible max-[901px]:data-[menu=fechado]:hidden"
            aria-label="Navegação principal"
          >
            {itens.map((item) =>
              item.subitens !== undefined ? (
                celular ? (
                  <SubmenuNaLista
                    key={item.chave}
                    item={item}
                    ativo={item.chave === atual}
                    subitemAtual={subitemAtual}
                    itemAtual={itemDoSubitemAtual}
                    classeDoTitulo={cn(ITEM_DA_FAIXA, "mb-0")}
                    classeDoSubitem={SUBITEM_NA_FAIXA}
                    classeDaGuia="border-(--azul-300)"
                    aoEscolher={(sub, alvo) => {
                      aoTrocarSubitem?.(item.chave, sub, alvo)
                      definirMenuAberto(false)
                    }}
                  />
                ) : (
                  <SubmenuFlutuante
                    key={item.chave}
                    item={item}
                    ativo={item.chave === atual}
                    subitemAtual={subitemAtual}
                    itemAtual={itemDoSubitemAtual}
                    lado="bottom"
                    classe={cn(ITEM_DA_FAIXA, "cursor-pointer bg-transparent")}
                    rotuloVisivel={<span>{item.rotulo}</span>}
                    aoEscolher={(sub, alvo) => aoTrocarSubitem?.(item.chave, sub, alvo)}
                  />
                )
              ) : (
              <a
                key={item.chave}
                className={ITEM_DA_FAIXA}
                href={`#${item.chave}`}
                aria-current={item.chave === atual ? "page" : undefined}
                aria-disabled={item.emBreve === true ? true : undefined}
                title={item.emBreve === true ? "Ainda não construído" : undefined}
                onClick={(evento) => {
                  evento.preventDefault()
                  if (item.emBreve !== true) {
                    aoTrocar(item.chave)
                    definirMenuAberto(false)
                  }
                }}
              >
                <item.icone tamanho={16} />
                <span>{item.rotulo}</span>
                {item.emBreve === true && (
                  <Selo situacao="em_breve" />
                )}
              </a>
              ),
            )}
          </nav>
        )}
      </header>

      {temTrilho && (
        <nav
          id="menu-principal"
          data-recolhido={recolhido}
          data-menu={menuAberto ? "aberto" : "fechado"}
          className={cn(
            "max-[901px]:data-[menu=fechado]:hidden group/trilho flex flex-col border-b border-(--borda) bg-(image:--fusao-realce) [grid-area:trilho] min-[901px]:border-b-0",
            trilhoADireita ? "min-[901px]:border-l" : "min-[901px]:border-r",
          )}
          aria-label="Navegação principal"
        >
          <div
            data-slot="conteudo-do-trilho"
            className="flex flex-col bg-(image:--fusao-realce) min-[901px]:sticky min-[901px]:top-0 min-[901px]:h-[calc(100vh-var(--faixa-altura))] min-[901px]:group-data-[recolhido=true]/trilho:pt-10"
          >
          {secoes.map((secao) => (
            <div key={secao.titulo} className="p-2">
              <div className="px-2 pt-2.5 pb-1.5 text-(length:--texto-xs) tracking-[0.12em] text-(--azul-200) uppercase min-[901px]:group-data-[recolhido=true]/trilho:hidden">
                {secao.titulo}
              </div>
              <div className="flex flex-col gap-1">
                {secao.itens.map((item) => {
                  const Icone = item.icone
                  if (item.subitens !== undefined) {
                    return (
                      celular ? (
                        <SubmenuNaLista
                          key={item.chave}
                          item={item}
                          ativo={item.chave === atual}
                          subitemAtual={subitemAtual}
                          itemAtual={itemDoSubitemAtual}
                          classeDoTitulo={cn(ITEM_DO_TRILHO, "font-semibold")}
                          classeDoSubitem={SUBITEM_NA_FAIXA}
                          classeDaGuia="border-(--azul-300)"
                          aoEscolher={(sub, alvo) => {
                            aoTrocarSubitem?.(item.chave, sub, alvo)
                            definirMenuAberto(false)
                          }}
                        />
                      ) : (
                        <SubmenuFlutuante
                          key={item.chave}
                          item={item}
                          ativo={item.chave === atual}
                          subitemAtual={subitemAtual}
                          itemAtual={itemDoSubitemAtual}
                          lado={trilhoADireita ? "left" : "right"}
                          classe={cn(ITEM_DO_TRILHO, "w-full cursor-pointer text-left")}
                          rotuloVisivel={<span className="min-[901px]:group-data-[recolhido=true]/trilho:hidden">{item.rotulo}</span>}
                          aoEscolher={(sub, alvo) => aoTrocarSubitem?.(item.chave, sub, alvo)}
                        />
                      )
                    )
                  }
                  return (
                    <a
                      key={item.chave}
                      className={ITEM_DO_TRILHO}
                      href={`#${item.chave}`}
                      aria-label={item.rotulo}
                      aria-current={item.chave === atual ? "page" : undefined}
                      aria-disabled={item.emBreve === true ? true : undefined}
                      title={item.emBreve === true ? `${item.rotulo} · Ainda não construído` : item.rotulo}
                      onClick={(evento) => {
                        evento.preventDefault()
                        if (item.emBreve !== true) {
                          aoTrocar(item.chave)
                          definirMenuAberto(false)
                        }
                      }}
                    >
                      <Icone tamanho={16} />
                      <span className="min-[901px]:group-data-[recolhido=true]/trilho:hidden">{item.rotulo}</span>
                      {item.emBreve === true && (
                        <Selo situacao="em_breve" classe="ml-auto min-[901px]:group-data-[recolhido=true]/trilho:hidden" />
                      )}
                    </a>
                  )
                })}
              </div>
            </div>
          ))}

          <div className="mt-auto truncate border-t border-(--azul-400) px-4 py-3.5 text-(length:--texto-xs) text-(--azul-100) min-[901px]:group-data-[recolhido=true]/trilho:px-1 min-[901px]:group-data-[recolhido=true]/trilho:text-center">
            {conta.entidade !== null ? conta.entidade.sigla : "Todas as entidades"}
          </div>

          <Button
            type="button"
            variant="default"
            size="icon"
            className={cn(
              "absolute top-[7px] z-10 size-8 rounded-full p-0 shadow-md ring-2 ring-background max-[901px]:hidden",
              trilhoADireita ? "-left-4 [&>svg]:-scale-x-100" : "-right-4",
            )}
            title={recolhido ? "Expande o menu e mostra os rótulos" : "Recolhe o menu, só os ícones ficam"}
            aria-label={recolhido ? "Expande o menu e mostra os rótulos" : "Recolhe o menu, só os ícones ficam"}
            aria-expanded={!recolhido}
            onClick={() => definirRecolhido((atual) => !atual)}
          >
            {recolhido ? <Icones.MostrarMenu tamanho={20} /> : <Icones.OcultarMenu tamanho={20} />}
          </Button>
          </div>
        </nav>
      )}

      <div className="flex min-w-0 flex-col [grid-area:corpo]">
        {(trilha.length > 0 || !acoesNaFaixa) && (
          <header
            data-slot="barra-da-trilha"
            className="flex min-h-(--barra-altura) min-w-0 flex-wrap items-center gap-3.5 border-b border-(--borda) bg-(--superficie) px-5 max-[767px]:px-3"
          >
            {trilha.length > 0 && <Trilha niveis={trilha} />}
            {!acoesNaFaixa && <div className="ml-auto flex items-center gap-2">{acoes}</div>}
          </header>
        )}
        <main className="mx-auto flex w-full flex-1 flex-col gap-2 px-(--margem) pt-2 pb-2">
          {conta.entidade?.desativada_em && (
            <p role="status" className={FAIXA_DA_ENTIDADE_DESATIVADA}>
              {`Esta entidade está desativada desde ${data(conta.entidade.desativada_em.slice(0, 10))}. Você pode consultar, mas nada pode ser salvo.`}
            </p>
          )}
          {children}
        </main>
      </div>
    </div>
  )
}

function Trilha({ niveis }: { niveis: NivelDaTrilha[] }) {
  return (
    <Breadcrumb>
      <BreadcrumbList className="gap-1.5 text-(length:--texto-sm) text-(--tinta-2) sm:gap-1.5">
        {niveis.map((nivel, indice) => {
          const ultimo = indice === niveis.length - 1
          return (
            <Fragment key={`${indice}-${nivel.rotulo}`}>
              <BreadcrumbItem>
                {ultimo ? (
                  <BreadcrumbPage className="font-[650] text-(--tinta)">{nivel.rotulo}</BreadcrumbPage>
                ) : nivel.endereco !== undefined ? (
                  <BreadcrumbLink
                    href={nivel.endereco}
                    className="underline-offset-2 hover:text-(--tinta) hover:underline focus-visible:outline-2 focus-visible:outline-(--foco)"
                    onClick={(evento) => {
                      if (evento.button !== 0 || evento.ctrlKey || evento.metaKey || evento.shiftKey || evento.altKey) return
                      evento.preventDefault()
                      nivel.aoAbrir?.()
                    }}
                  >
                    {nivel.rotulo}
                  </BreadcrumbLink>
                ) : (
                  <span>{nivel.rotulo}</span>
                )}
              </BreadcrumbItem>
              {!ultimo && <BreadcrumbSeparator />}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
